import { useMemo } from 'react';
import {
  areAllVariablesCurrencyType,
  CurrencyConfig,
  getLatestActualUtrvCurrency,
  getMetricUnit,
  isCurrencyPrefix,
} from './utils';
import { GridDashboardChartItem, HistoricalUtrs } from '../../../../../types/insight-custom-dashboard';
import { SingleUtrSingleValue } from './SingleUtrSingleValue';
import { MultiUtrsSingleValue } from './MultiUtrsSingleValue';
import { ChartUtrData } from '../../types';

type Props = Pick<GridDashboardChartItem, 'calculation' | 'unitText' | 'variables' | 'note'> & {
  utrsData: ChartUtrData[];
  handleSetTarget?: () => void;
  hasSparkLine?: boolean;
  readOnly: boolean;
  altUnitText: string; // TODO: Should be removed when refactoring unitText
  currencyConfig: CurrencyConfig;
};

export const SingleValue = (props: Props) => {
  const {
    utrsData,
    calculation,
    unitText,
    variables,
    note,
    hasSparkLine = false,
    readOnly,
    handleSetTarget,
    altUnitText,
    currencyConfig,
  } = props;

  const isCurrency = areAllVariablesCurrencyType(variables, utrsData);

  // For single-value widgets, look up currency from the specific UTRV being displayed
  // This handles mixed currencies (e.g., aggregated surveys with different currency configs)
  const currencySymbol = useMemo(() => {
    // If we already have a consistent currency symbol, use it
    if (currencyConfig.currencySymbol) {
      return currencyConfig.currencySymbol;
    }
    const variableValues = variables ? Object.values(variables) : [];
    // For non-currency metrics or multi-variable, no currency lookup needed
    if (!isCurrency || variableValues.length !== 1) {
      return undefined;
    }
    // Find the latest actual UTRV for this variable and look up its currency
    const variableCode = variableValues[0].code;
    const utrData = utrsData.find((data) => data.utr.code === variableCode) as HistoricalUtrs | undefined;
    return getLatestActualUtrvCurrency(utrData?.utrvs, currencyConfig.surveyUnitConfigMap)?.currencySymbol;
  }, [currencyConfig.currencySymbol, currencyConfig.surveyUnitConfigMap, isCurrency, variables, utrsData]);

  const variableValues = variables ? Object.values(variables) : [];

  if (!utrsData.length || !variables || !calculation) {
    return null;
  }

  const baseMetricUnit = unitText || getMetricUnit(utrsData);
  // For currency metrics: only use currencySymbol from currencyConfig (don't fall back to UTR unit which may be wrong)
  // For non-currency metrics: use baseMetricUnit as normal
  const metricUnit = isCurrency ? (currencySymbol || '') : baseMetricUnit;
  // Percentage is always suffix, currency depends on symbol type ($ prefix, CHF suffix)
  const isSuffix = metricUnit === '%' || (isCurrency && !isCurrencyPrefix(currencySymbol));
  const decimalPlaces = calculation.values[0].decimalPlaces;

  if (variableValues.length === 1) {
    return (
      <SingleUtrSingleValue
        altUnitText={altUnitText}
        calculation={calculation}
        isSuffix={isSuffix}
        isCurrency={isCurrency}
        decimalPlaces={decimalPlaces}
        metricUnit={metricUnit}
        readOnly={readOnly}
        utrsData={utrsData}
        variableValue={variableValues[0]}
        variables={variables}
        handleSetTarget={handleSetTarget}
        note={note}
        hasSparkLine={hasSparkLine}
      />
    );
  }

  return (
    <MultiUtrsSingleValue
      unitText={unitText}
      calculation={calculation}
      isSuffix={isSuffix}
      isCurrency={isCurrency}
      decimalPlaces={decimalPlaces}
      metricUnit={metricUnit}
      utrsData={utrsData}
      variables={variables}
      note={note}
      hasSparkLine={hasSparkLine}
    />
  );
};
